import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PromptFormData {
  title: string;
  description: string;
  prompt_content: string;
}

interface AddPromptFormProps {
  /** Si fourni, le formulaire est en mode édition */
  initialValues?: PromptFormData;
  onSave: (data: { title: string; description?: string; prompt_content: string }) => Promise<void>;
  onCancel: () => void;
  saveLabel?: string;
}

export function AddPromptForm({
  initialValues,
  onSave,
  onCancel,
  saveLabel = 'Ajouter',
}: AddPromptFormProps) {
  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [promptContent, setPromptContent] = useState(initialValues?.prompt_content ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; prompt_content?: string }>({});

  const validate = () => {
    const next: typeof errors = {};
    if (!title.trim()) next.title = 'Le titre est requis';
    if (!promptContent.trim()) next.prompt_content = 'Le contenu du prompt est requis';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        prompt_content: promptContent.trim(),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = (hasError?: string) =>
    cn(
      'w-full border rounded-lg px-3 py-2 text-sm text-grey-900 placeholder-grey-400 outline-none transition-colors bg-white',
      hasError
        ? 'border-red-300 focus:border-red-500'
        : 'border-grey-200 focus:border-charcoal',
    );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="block text-xs font-medium text-grey-600 mb-1.5">
          Titre <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setErrors((p) => ({ ...p, title: undefined })); }}
          placeholder="Ex : Analyser le contrat"
          className={inputClass(errors.title)}
          disabled={isSaving}
          autoFocus
        />
        {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-grey-600 mb-1.5">
          Description courte <span className="text-grey-400">(optionnel)</span>
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ex : Synthèse des clauses essentielles"
          className={inputClass()}
          disabled={isSaving}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-grey-600 mb-1.5">
          Contenu du prompt <span className="text-red-400">*</span>
        </label>
        <textarea
          value={promptContent}
          onChange={(e) => { setPromptContent(e.target.value); setErrors((p) => ({ ...p, prompt_content: undefined })); }}
          placeholder="Rédigez le texte qui sera inséré dans le champ de saisie..."
          rows={8}
          className={cn(inputClass(errors.prompt_content), 'resize-y min-h-[160px]')}
          disabled={isSaving}
        />
        {errors.prompt_content && <p className="text-xs text-red-500 mt-1">{errors.prompt_content}</p>}
      </div>

      <div className="flex gap-2 justify-end pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="text-sm px-3 py-1.5 rounded-lg bg-grey-100 text-grey-700 hover:bg-grey-200 transition-colors disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="text-sm px-3 py-1.5 rounded-lg bg-charcoal text-white hover:bg-charcoal/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {saveLabel}
        </button>
      </div>
    </div>
  );
}
